import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useWalletConnect } from "@btc-vision/walletconnect";
import {
  useEscrow,
  useEscrowTxHistory,
  useActivateEscrow,
  useReleaseFunds,
  useRaiseDispute,
  useResolveDispute,
  useExpireEscrow,
  useFundEscrow,
  useTokenBalance,
  useTokenAllowance,
  useTokenMetadata,
  formatTokenAmount,
  getStateName,
  getStateColor,
  STATE_CREATED,
  STATE_FUNDED,
  STATE_ACTIVE,
  STATE_RELEASED,
  STATE_DISPUTED,
  STATE_RESOLVED,
} from "../hooks/useEscrow";
import { MOTOSETTLE_CONFIG } from "../config";

export function EscrowDetailPage() {
  const { id } = useParams();
  const { walletAddress, address } = useWalletConnect();
  // Derive hex form of our wallet for comparison with escrow fields (which are hex).
  const walletHex = (address as any)?.toString?.()?.toLowerCase() ?? "";
  const escrowId = id ? BigInt(id) : undefined;

  const { data: escrow, isLoading, error, refetch } = useEscrow(escrowId);

  // All hooks must be called before any early return (React rules of hooks)
  const createdAtBlock = escrow ? (escrow.createdAt as bigint) : undefined;
  const { data: txHistory } = useEscrowTxHistory(createdAtBlock, escrowId);

  const tokenAddr = escrow?.token?.toString?.() ?? (escrow?.token as string | undefined);
  const { data: tokenMeta } = useTokenMetadata(tokenAddr);
  const symbol = tokenMeta?.symbol ?? "";
  const decimals = tokenMeta?.decimals ?? 8;
  const fmt = (v: bigint) => `${formatTokenAmount(v, decimals)}${symbol ? " " + symbol : ""}`;
  const { data: tokenBalance } = useTokenBalance(tokenAddr, walletAddress ?? undefined);
  const { data: tokenAllowance } = useTokenAllowance(
    tokenAddr,
    walletAddress ?? undefined,
    MOTOSETTLE_CONFIG.escrowCore,
  );

  const activateMut = useActivateEscrow();
  const releaseMut = useReleaseFunds();
  const disputeMut = useRaiseDispute();
  const resolveMut = useResolveDispute();
  const expireMut = useExpireEscrow();
  const fundMut = useFundEscrow();

  const [buyerPct, setBuyerPct] = useState(50);
  const [txStatus, setTxStatus] = useState<string>("");

  if (!id) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Escrow no encontrado</h1>
          <Link to="/escrows">&larr; Volver a escrows</Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Escrow #{id}</h1>
          <p style={{ color: "var(--text-muted)" }}>Cargando datos del contrato...</p>
        </div>
        <div className="card" style={{ padding: 40 }}>
          <div className="skeleton" style={{ height: 20, width: 300, marginBottom: 12 }} />
          <div className="skeleton" style={{ height: 16, width: 400, marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 16, width: 350 }} />
        </div>
      </div>
    );
  }

  if (error || !escrow) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Escrow #{id}</h1>
        </div>
        <div className="alert alert-error" style={{ maxWidth: 600 }}>
          {error ? `Error: ${(error as Error).message}` : "Escrow no encontrado en el contrato."}
        </div>
        <Link to="/escrows" style={{ marginTop: 16, display: "inline-block" }}>
          &larr; Volver a escrows
        </Link>
      </div>
    );
  }

  const state = Number(escrow.state);
  const stateName = getStateName(state);
  const stateColor = getStateColor(state);

  const buyer = escrow.buyer?.toString?.() ?? escrow.buyer ?? "";
  const seller = escrow.seller?.toString?.() ?? escrow.seller ?? "";
  const arbitrator = escrow.arbitrator?.toString?.() ?? escrow.arbitrator ?? "";
  const token = escrow.token?.toString?.() ?? escrow.token ?? "";
  const amount = escrow.amount as bigint;
  const fee = escrow.fee as bigint;
  const fundDeadline = escrow.fundDeadline as bigint;
  const tradeDeadline = escrow.tradeDeadline as bigint;
  const createdAt = escrow.createdAt as bigint;

  // Detect empty/non-existent escrow (contract returns zeros for invalid IDs)
  const isEmpty = /^0x0+$/.test(buyer.toLowerCase()) && createdAt === 0n && amount === 0n;
  if (isEmpty) {
    return (
      <div className="page">
        <div style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 16 }}>
          <Link to="/escrows" style={{ color: "var(--text-muted)", fontSize: 14 }}>
            &larr; Escrows
          </Link>
          <h1 style={{ margin: 0 }}>Escrow #{id}</h1>
        </div>
        <div className="alert alert-error" style={{ maxWidth: 600 }}>
          Este escrow no existe en el contrato. El contrato devolvio datos vacios (buyer 0x0, monto 0, bloque 0).
          <br /><br />
          Posibles causas:
          <ul style={{ marginTop: 8, marginLeft: 20 }}>
            <li>El ID que buscas no corresponde a ningun escrow creado</li>
            <li>El frontend esta apuntando al contrato equivocado (revisa la consola: <code>[MotoSettle Config]</code>)</li>
            <li>La transaccion de creacion nunca llego a confirmarse on-chain</li>
          </ul>
        </div>
      </div>
    );
  }

  // Role detection — escrow fields are hex pubkeys; compare with wallet hex (not bech32).
  const matchAddr = (field: string): boolean => {
    if (!walletHex || !field) return false;
    const f = field.toLowerCase();
    // Compare full hex, or fall back to last 16 hex chars if formats differ slightly.
    return f === walletHex || f.includes(walletHex.replace(/^0x/, "").slice(-16));
  };
  const isBuyer = matchAddr(buyer);
  const isSeller = matchAddr(seller);
  const isArbitrator = matchAddr(arbitrator);

  const myRole = isBuyer ? "Buyer" : isSeller ? "Seller" : isArbitrator ? "Arbitrador" : null;

  const handleAction = async (
    label: string,
    action: () => Promise<any>,
  ) => {
    setTxStatus(`${label}...`);
    try {
      const result = await action();
      const txId = result?.transactionId;
      setTxStatus(`${label} enviado!${txId ? ` Tx: ${txId}` : ""} Esperando confirmacion...`);
      setTimeout(() => {
        refetch();
        setTxStatus("");
      }, 5000);
    } catch (e: any) {
      setTxStatus(`Error: ${e.message}`);
    }
  };

  const anyMutLoading =
    activateMut.isPending ||
    releaseMut.isPending ||
    disputeMut.isPending ||
    resolveMut.isPending ||
    expireMut.isPending ||
    fundMut.isPending;

  return (
    <div className="page">
      {/* Header */}
      <div style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 16 }}>
        <Link to="/escrows" style={{ color: "var(--text-muted)", fontSize: 14 }}>
          &larr; Escrows
        </Link>
        <h1 style={{ margin: 0 }}>Escrow #{id}</h1>
        <div
          style={{
            padding: "5px 14px",
            borderRadius: "var(--radius-pill)",
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: stateColor,
            background: `${stateColor}22`,
            border: `1px solid ${stateColor}44`,
          }}
        >
          {stateName}
        </div>
        {myRole && (
          <div
            style={{
              padding: "4px 10px",
              borderRadius: "var(--radius-pill)",
              fontSize: 11,
              fontWeight: 600,
              color: "var(--purple-light)",
              background: "rgba(111,76,255,0.12)",
              border: "1px solid rgba(111,76,255,0.25)",
            }}
          >
            Tu rol: {myRole}
          </div>
        )}
      </div>

      {/* Main grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        {/* Participants */}
        <div
          className="card"
          style={{
            padding: 20,
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
          }}
        >
          <h3 style={{ marginBottom: 16, fontSize: 14, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Participantes
          </h3>
          <InfoRow label="Buyer" value={buyer} isSelf={isBuyer} />
          <InfoRow label="Seller" value={seller} isSelf={isSeller} />
          <InfoRow label="Arbitrador" value={arbitrator} isSelf={isArbitrator} />
          <InfoRow label="Token" value={symbol ? `${symbol} (${token.slice(0, 10)}...)` : token} />
        </div>

        {/* Financials */}
        <div
          className="card"
          style={{
            padding: 20,
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
          }}
        >
          <h3 style={{ marginBottom: 16, fontSize: 14, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Detalles
          </h3>
          <InfoRow label="Monto" value={fmt(amount)} />
          <InfoRow label="Fee" value={fmt(fee)} />
          <InfoRow label="Fund Deadline" value={`Bloque ${fundDeadline.toString()}`} />
          <InfoRow label="Trade Deadline" value={`Bloque ${tradeDeadline.toString()}`} />
          <InfoRow label="Creado en" value={`Bloque ${createdAt.toString()}`} />
        </div>
      </div>

      {/* Transaction IDs — fetched from blockchain */}
      {txHistory && txHistory.length > 0 && (
        <div
          className="card"
          style={{
            padding: 20,
            marginBottom: 24,
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
          }}
        >
          <h3 style={{ marginBottom: 16, fontSize: 14, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Transacciones (on-chain)
          </h3>
          {txHistory.map((tx, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                padding: "6px 0",
                borderBottom: i < txHistory.length - 1 ? "1px solid var(--border)" : "none",
              }}
            >
              <div
                style={{
                  minWidth: 90,
                  fontSize: 12,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: "var(--radius-pill)",
                  background: tx.source === "on-chain" ? "rgba(46,204,113,0.15)" : "rgba(111,76,255,0.15)",
                  color: tx.source === "on-chain" ? "var(--success)" : "var(--purple-light)",
                  textAlign: "center",
                }}
                title={tx.source === "on-chain" ? "Indexada desde la blockchain" : "Guardada localmente en este navegador"}
              >
                {tx.label}
              </div>
              <div style={{ flex: 1, fontSize: 12, fontFamily: "var(--font-mono)", wordBreak: "break-all", color: "var(--text-secondary)" }}>
                {tx.txId}
              </div>
            </div>
          ))}
          <div style={{ marginTop: 12, fontSize: 11, color: "var(--text-muted)" }}>
            Las tx on-chain (verdes) son visibles para todos. Las locales (moradas) solo en este navegador — el indexer The Graph las hara publicas.
          </div>
        </div>
      )}

      {/* Resolution info (if resolved) */}
      {(state === STATE_RELEASED || state === STATE_RESOLVED) && (
        <div
          className="card"
          style={{
            padding: 16,
            marginBottom: 24,
            background: "rgba(46,204,113,0.06)",
            border: "1px solid rgba(46,204,113,0.2)",
            borderRadius: "var(--radius-md)",
          }}
        >
          <h3 style={{ fontSize: 13, color: "var(--success)", marginBottom: 8 }}>
            Resolucion
          </h3>
          <div style={{ fontSize: 13, fontFamily: "var(--font-mono)" }}>
            Buyer recibio: {fmt(escrow.buyerResolved as bigint)} | Seller recibio: {fmt(escrow.sellerResolved as bigint)}
          </div>
        </div>
      )}

      {/* Actions */}
      {walletAddress && (
        <div
          className="card"
          style={{
            padding: 20,
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
            marginBottom: 24,
          }}
        >
          <h3 style={{ marginBottom: 16, fontSize: 14, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Acciones
          </h3>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            {/* Fund — buyer can fund a Created escrow */}
            {state === STATE_CREATED && isBuyer && (() => {
              const total = amount + fee;
              const hasBalance = tokenBalance !== undefined && tokenBalance >= total;
              const needsApprove = tokenAllowance !== undefined && tokenAllowance < total;
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                    Requerido: {fmt(total)} &nbsp;|&nbsp;
                    Balance: {tokenBalance !== undefined ? fmt(tokenBalance) : "..."} &nbsp;|&nbsp;
                    Allowance: {tokenAllowance !== undefined ? fmt(tokenAllowance) : "..."}
                  </div>
                  <button
                    className="btn btn-primary"
                    disabled={anyMutLoading || !hasBalance}
                    onClick={() =>
                      handleAction("Fund Escrow", () =>
                        fundMut.mutateAsync({
                          escrowId: escrowId!,
                          motoBalance: 0n,
                          token: tokenAddr!,
                          total,
                          onStep: (step) => {
                            const labels: Record<string, string> = {
                              check: "Verificando balance y allowance...",
                              approve: "Firma el approve en la wallet...",
                              waitApprove: "Esperando confirmacion on-chain del approve...",
                              fund: "Firma el fundEscrow en la wallet...",
                              done: "Completado",
                            };
                            setTxStatus(labels[step] ?? step);
                          },
                        }),
                      )
                    }
                  >
                    {!hasBalance
                      ? "Balance insuficiente"
                      : needsApprove
                        ? "Aprobar + Fondear"
                        : "Fund Escrow"}
                  </button>
                </div>
              );
            })()}

            {/* Activate — seller can activate a Funded escrow */}
            {state === STATE_FUNDED && isSeller && (
              <button
                className="btn btn-primary"
                disabled={anyMutLoading}
                onClick={() =>
                  handleAction("Activate Escrow", () =>
                    activateMut.mutateAsync(escrowId!),
                  )
                }
              >
                Activar Escrow
              </button>
            )}

            {/* Release — buyer releases funds to seller */}
            {state === STATE_ACTIVE && isBuyer && (
              <button
                className="btn btn-primary"
                disabled={anyMutLoading}
                onClick={() =>
                  handleAction("Release Funds", () =>
                    releaseMut.mutateAsync(escrowId!),
                  )
                }
              >
                Liberar Fondos (Release)
              </button>
            )}

            {/* Raise Dispute — buyer or seller can dispute an Active escrow */}
            {state === STATE_ACTIVE && (isBuyer || isSeller) && (
              <button
                className="btn btn-secondary"
                disabled={anyMutLoading}
                onClick={() =>
                  handleAction("Raise Dispute", () =>
                    disputeMut.mutateAsync(escrowId!),
                  )
                }
                style={{ borderColor: "var(--danger)", color: "var(--danger)" }}
              >
                Abrir Disputa
              </button>
            )}

            {/* Resolve Dispute — arbitrator resolves */}
            {state === STATE_DISPUTED && isArbitrator && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <label style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                  % para Buyer:
                </label>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={buyerPct}
                  onChange={(e) => setBuyerPct(Number(e.target.value))}
                  style={{ width: 120 }}
                />
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, minWidth: 40 }}>
                  {buyerPct}%
                </span>
                <button
                  className="btn btn-primary"
                  disabled={anyMutLoading}
                  onClick={() =>
                    handleAction("Resolve Dispute", () =>
                      resolveMut.mutateAsync({
                        escrowId: escrowId!,
                        buyerPct,
                      }),
                    )
                  }
                >
                  Resolver Disputa
                </button>
              </div>
            )}

            {/* Expire — anyone can expire if past deadline */}
            {(state === STATE_CREATED || state === STATE_FUNDED || state === STATE_ACTIVE) && (
              <button
                className="btn btn-ghost"
                disabled={anyMutLoading}
                onClick={() =>
                  handleAction("Expire Escrow", () =>
                    expireMut.mutateAsync(escrowId!),
                  )
                }
                style={{ fontSize: 12 }}
              >
                Expirar (si paso deadline)
              </button>
            )}

            {/* Terminal states */}
            {(state >= 4) && (
              <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
                Este escrow esta en estado final: {stateName}
              </span>
            )}
          </div>

          {/* Status message */}
          {txStatus && (
            <div
              className={`alert ${
                txStatus.includes("Error")
                  ? "alert-error"
                  : txStatus.includes("enviado")
                  ? "alert-success"
                  : "alert-info"
              }`}
              style={{ marginTop: 12 }}
            >
              {txStatus}
            </div>
          )}
        </div>
      )}

      {!walletAddress && (
        <div
          className="card"
          style={{
            padding: 20,
            textAlign: "center",
            color: "var(--text-muted)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
          }}
        >
          Conecta tu wallet para ver acciones disponibles
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function InfoRow({
  label,
  value,
  isSelf,
}: {
  label: string;
  value: string;
  isSelf?: boolean;
}) {
  return (
    <div style={{ marginBottom: 10, display: "flex", alignItems: "baseline", gap: 8 }}>
      <span style={{ fontSize: 12, color: "var(--text-muted)", minWidth: 90, flexShrink: 0 }}>
        {label}:
      </span>
      <span
        style={{
          fontSize: 12,
          fontFamily: "var(--font-mono)",
          color: isSelf ? "var(--purple-light)" : "var(--text-secondary)",
          wordBreak: "break-all",
        }}
      >
        {value || "---"}
        {isSelf && (
          <span style={{ marginLeft: 6, fontSize: 10, color: "var(--purple-light)" }}>(tu)</span>
        )}
      </span>
    </div>
  );
}

function formatAmount(val: bigint): string {
  if (val >= 100_000_000n) {
    const whole = val / 100_000_000n;
    const frac = val % 100_000_000n;
    const fracStr = frac.toString().padStart(8, "0").replace(/0+$/, "");
    return fracStr ? `${whole}.${fracStr}` : whole.toString();
  }
  return val.toString();
}
